<!-- eslint-disable unused-imports/no-unused-vars -->
<script lang="ts" setup>
import { useToast } from '@solfacil/girassol'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CheckIcon from '~icons/material-symbols/check-circle-outline-rounded'
import SuportIcon from '~icons/material-symbols/contact-support-outline-rounded'
import RefreshIcon from '~icons/material-symbols/refresh-rounded'
import useBiometricValidation from '~/store-v2/installation/useBiometricValidation'
import useCommissioningResetCustomerRejection from '~/store-v2/installation/useCommissioningResetCustomerRejection'
import useInstallation from '~/store-v2/installation/useInstallation'
import useInstallationSectionDelete from '~/store-v2/installation/useSectionDelete'
import date from '~/utils/date'
import useInstallationAlerts from './composables/useInstallationAlerts'
import useInstallationAnalysis, { type InstallationAnalysisErrorStatus } from './composables/useInstallationAnalysis'
import { useInstallationCommissioningFlowInject } from './useInstallationCommissioningFlow'
import useInstallationFlow, { steps } from './useInstallationFlow'

const router = useRouter()
const route = useRoute<any>()
const { t } = useI18n()
const toast = useToast()

const id = computed(() => route.params.id as string)
const { handleStepChange, setLastError } = useInstallationFlow(id)
const itemsContainerRef = useTemplateRef<HTMLElement | null>('status-container')
const commissioningFlow = useInstallationCommissioningFlowInject()

// Data fetching
const { data: installation, isError: isErrorInstallation } = useInstallation(id)
const installationId = computed(() => installation.value?.id)
const { mutateAsync: sectionDelete } = useInstallationSectionDelete(installationId, id)
const { mutateAsync: resetCustomerRejection } = useCommissioningResetCustomerRejection(id)
const { mutateAsync: biometricValidation } = useBiometricValidation(id.value)

const {
  isStatusAnalysisError,
  isFatalError,
  getErrorConfig,
} = useInstallationAnalysis()

const {
  showSuccessToast,
  showErrorToast,
} = useInstallationAlerts()

// Business logic
const currentStage = computed(() => commissioningFlow?.analysisStage.value ?? 0)

const statusByStep = computed(() => (currentStage.value >= 2 ? 'DONE' : 'WAITING'))

const stepInError = computed((): InstallationAnalysisErrorStatus | null => {
  const error = commissioningFlow?.analysisError.value
  if (!error || !isStatusAnalysisError(error))
    return null

  const neutralStatuses: InstallationAnalysisErrorStatus[] = [
    'WAITING_PARTNER_CONFIRMATION',
    'WAITING_GENERATION_CHECK',
    'WAITING_CUSTOMER_CONFIRMATION',
    'APPROVED',
  ]
  if (neutralStatuses.includes(error))
    return null

  return error
})

// Loading and error states
const isLoading = computed(() =>
  !installation.value
  || !commissioningFlow?.commissioningPayload.value
  || currentStage.value < 2
  || !currentStage.value,
)

const isDeletingSection = ref(false)
const isResendingWhatsApp = ref(false)
const isBlocked = ref(false)
const countdownSeconds = ref(0)
const countdownInterval = ref<NodeJS.Timeout | null>(null)

// Customer data
const customerPhone = computed(() => installation.value?.stage_customer_phone || '')

const latestCustomerValidationRequest = computed(
  () => commissioningFlow?.commissioningPayload.value?.data?.customer_validation_requests?.[0] ?? null,
)

const formattedDate = computed(() => {
  const dateValue = latestCustomerValidationRequest.value?.initiated_at
  if (!dateValue)
    return 'N/A'

  return date(dateValue).format('LLL')
})

const baseUrl = import.meta.env.BASE_URL

const rawSteps = [
  { key: 'simulation', title: 'Inversor', boxTitle: 'Validação do inversor', icon: 'step1.svg' },
  { key: 'proposal', title: 'Cliente', boxTitle: 'Confirmação do cliente', icon: 'step2.svg' },
  { key: 'installation', title: 'Instalação', boxTitle: 'Instalação validada', icon: 'step3.svg' },
]

// Helper functions
function getStepState(stage: number, currentStage: number, stepInError: InstallationAnalysisErrorStatus | null) {
  const errorConfig = stepInError ? getErrorConfig(stepInError) : null
  const hasError = errorConfig?.stage === stage && currentStage === stage

  return {
    hasError: Boolean(hasError),
    isCompleted: stage < currentStage,
    isCurrent: stage === currentStage,
    errorConfig: hasError ? errorConfig : null,
  }
}

function getStepTitle(step: any, stepState: any) {
  if (stepState.hasError && stepState.errorConfig?.customTitle) {
    return {
      waiting: stepState.errorConfig.customTitle,
      done: `${step.boxTitle} validado`,
    }
  }

  const stepNumber = step.key === 'simulation' ? 1 : step.key === 'proposal' ? 2 : 3

  if (stepNumber === 1) {
    return {
      waiting: 'Validação do inversor',
      done: 'Inversor validado',
    }
  }

  if (stepNumber === 2) {
    return {
      waiting: 'Confirmação do cliente',
      done: 'Instalação confirmada',
    }
  }

  if (stepNumber === 3) {
    return {
      waiting: 'Instalação validada',
      done: 'Instalação validada', // Não muda
    }
  }

  // Fallback para casos não previstos
  return {
    waiting: step.boxTitle,
    done: `${step.boxTitle} validado`,
  }
}

function getStepIcon(step: any, stepState: any): string {
  const iconSuffix = stepState.hasError ? stepState.errorConfig?.iconSuffix || '' : ''
  const iconName = step.icon.replace('.svg', `${iconSuffix}.svg`)
  return `${baseUrl}images/installation/under-analysis-${iconName}`
}

function formatPhoneNumber(phone: string): string {
  if (!phone) return ''

  const cleanPhone = phone.replace(/\D/g, '')

  // Celular com 9 (11 dígitos)
  if (cleanPhone.length === 11) {
    return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 3)} ${cleanPhone.slice(3, 7)}-${cleanPhone.slice(7)}`
  }

  // Telefone fixo (10 dígitos)
  if (cleanPhone.length === 10) {
    return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 6)}-${cleanPhone.slice(6)}`
  }

  // Com código do país +55 (13 dígitos)
  if (cleanPhone.length === 13 && cleanPhone.startsWith('55')) {
    const withoutCountryCode = cleanPhone.slice(2)
    return `(${withoutCountryCode.slice(0, 2)}) ${withoutCountryCode.slice(2, 3)} ${withoutCountryCode.slice(3, 7)}-${withoutCountryCode.slice(7)}`
  }

  // Com código do país sem + (12 dígitos)
  if (cleanPhone.length === 12 && cleanPhone.startsWith('55')) {
    const withoutCountryCode = cleanPhone.slice(2)
    return `(${withoutCountryCode.slice(0, 2)}) ${withoutCountryCode.slice(2, 6)}-${withoutCountryCode.slice(6)}`
  }

  return phone
}

function getHeaderTexts(currentStage: number, stepInError: InstallationAnalysisErrorStatus | null) {
  const errorConfig = stepInError ? getErrorConfig(stepInError) : null
  const hasHeaderError = errorConfig?.stage === currentStage && errorConfig?.customHeaderTitle

  if (hasHeaderError) {
    return {
      title: errorConfig.customHeaderTitle,
      description: errorConfig.customHeaderDescription,
    }
  }

  // Stage 2 sem erro - aguardando confirmação do cliente
  if (currentStage === 2 && stepInError !== 'REJECTED_BY_CUSTOMER') {
    return {
      title: 'Você está quase lá!',
      description: `Enviamos uma mensagem via Whatsapp para ${formatPhoneNumber(customerPhone.value)} solicitando a confirmação da instalação`,
    }
  }

  return {
    title: 'Inversor em processo de validação',
    description: 'Não se preocupe! Este processo é automático, mas pode demorar algumas horas.<br>Certifique-se de que os inversores estejam conectados',
  }
}

function scrollToCurrentStep(step: number): void {
  const statusContainer = itemsContainerRef.value
  if (statusContainer) {
    statusContainer.scrollTo({
      left: (statusContainer.scrollWidth / 3) * step,
      behavior: 'smooth',
    })
  }
}

// Computed properties
const headerTexts = computed(() => getHeaderTexts(currentStage.value, stepInError.value))

const countdownFormatted = computed(() => {
  const minutes = Math.floor(countdownSeconds.value / 60)
  const seconds = countdownSeconds.value % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
})

const showWhatsAppButton = computed(() =>
  currentStage.value === 2
  && stepInError.value !== 'REJECTED_BY_CUSTOMER',
)

const showCountdown = computed(() =>
  currentStage.value === 2
  && stepInError.value !== 'REJECTED_BY_CUSTOMER'
  && isBlocked.value
  && countdownSeconds.value > 0,
)

const stepsPage = computed(() =>
  rawSteps.map((step, index) => {
    const stage = index + 1
    const stepState = getStepState(stage, currentStage.value, stepInError.value)

    return {
      ...step,
      stage,
      title: getStepTitle(step, stepState),
      icon: getStepIcon(step, stepState),
      status: statusByStep.value,
      hasError: stepState.hasError,
      errorType: stepState.hasError ? stepInError.value : null,
      stepState,
    }
  }),
)

const stepData = computed(() =>
  rawSteps.map((step, index) => {
    const stage = index + 1
    const stepState = getStepState(stage, currentStage.value, stepInError.value)

    return {
      key: step.key,
      text: `Etapa ${stage}`,
      subtitle: step.title,
      current: currentStage.value === stage,
      warning: stepState.hasError,
    }
  }),
)

// Actions
function redirectProjects(): void {
  router.push('/projects')
}

function handleSupportClick(): void {
  // Redirecionar para site externo de suporte
  window.open('https://solfacil.com.br/suporte', '_blank')
}

function startCountdown(seconds: number): void {
  isBlocked.value = true
  countdownSeconds.value = seconds

  countdownInterval.value = setInterval(() => {
    countdownSeconds.value--

    if (countdownSeconds.value <= 0) {
      stopCountdown()
    }
  }, 1000)
}

function stopCountdown(): void {
  isBlocked.value = false
  countdownSeconds.value = 0

  if (countdownInterval.value) {
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }
}

async function handleWhatsAppClick(): Promise<void> {
  const BIOMETRIC_MOCK = false

  try {
    isResendingWhatsApp.value = true

    if (BIOMETRIC_MOCK) {
      const MOCK_RESPONSES = [
        {
          status: 'sent',
          retry_after_seconds: 180, // 3 minutos de cooldown após sucesso
          retry_at: null,
          reason: 'success',
        },
        {
          status: 'blocked',
          retry_after_seconds: 300, // 5 minutos
          retry_at: null,
          reason: 'rate_limit',
        },
        {
          status: 'blocked',
          retry_after_seconds: null,
          retry_at: '2024-01-15T14:30:00Z',
          reason: 'daily_quota_reached',
        },
        {
          status: 'blocked',
          retry_after_seconds: 1800, // 30 minutos
          retry_at: '2024-01-15T15:00:00Z',
          reason: 'rate_limit',
        },
        {
          status: 'sent',
          retry_after_seconds: null,
          retry_at: '2024-01-15T14:30:00Z', // Cooldown com horário específico
          reason: 'success',
        },
      ]

      // Change index to test different scenarios: 0=sent with cooldown, 1-3=blocked, 4=sent with time
      const mockIndex = 0 // Testando sucesso com cooldown
      const result = MOCK_RESPONSES[mockIndex] as any

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000))

      const status = result.status

      if (status === 'sent') {
        showSuccessToast(
          'Solicitação de confirmação enviada',
          'Enviamos uma mensagem para o seu cliente solicitando a confirmação da sua instalação',
        )

        // Verifica se há dados de retry mesmo no sucesso
        const retryAfter = result.retry_after_seconds
        const retryAt = result.retry_at

        if (retryAfter && retryAfter > 0) {
          startCountdown(retryAfter)
        }
        else if (retryAt) {
          const now = new Date()
          const retryTime = new Date(retryAt)
          const diffSeconds = Math.max(0, Math.floor((retryTime.getTime() - now.getTime()) / 1000))

          if (diffSeconds > 0) {
            startCountdown(diffSeconds)
          }
        }
      }
      else if (status === 'blocked') {
        const retryAfter = result.retry_after_seconds
        const retryAt = result.retry_at
        const reason = result.reason

        let description = 'Aguarde um momento antes de tentar novamente.'

        if (retryAfter) {
          const minutes = Math.ceil(retryAfter / 60)
          description = `Aguarde ${minutes} minuto${minutes > 1 ? 's' : ''} antes de tentar novamente.`

          // Inicia o cronômetro se temos retry_after_seconds
          startCountdown(retryAfter)
        }
        else if (retryAt) {
          const retryDate = date(retryAt).format('HH:mm')
          description = `Você poderá reenviar a solicitação às ${retryDate}.`

          // Calcula a diferença em segundos até o horário de retry
          const now = new Date()
          const retryTime = new Date(retryAt)
          const diffSeconds = Math.max(0, Math.floor((retryTime.getTime() - now.getTime()) / 1000))

          if (diffSeconds > 0) {
            startCountdown(diffSeconds)
          }
        }

        if (reason) {
          let reasonText = ''

          switch (reason) {
            case 'daily_quota_reached':
              reasonText = 'Limite diário de solicitações atingido.'
              break
            case 'rate_limit':
              reasonText = 'Muitas tentativas em pouco tempo.'
              break
            case 'success':
              reasonText = 'Solicitação enviada com sucesso.'
              break
            default:
              reasonText = reason
          }

          description = `${reasonText} ${description}`
        }

        showErrorToast('Solicitação bloqueada temporariamente', description)
      }
      else {
        showErrorToast('Erro ao reenviar solicitação. Tente novamente.')
      }
    }
    else {
      const result = await biometricValidation()
      const status = result?.status

      if (status === 'sent') {
        showSuccessToast(
          'Solicitação de confirmação enviada',
          'Enviamos uma mensagem para o seu cliente solicitando a confirmação da sua instalação',
        )

        // Verifica se há dados de retry mesmo no sucesso
        const retryAfter = result.retry_after_seconds
        const retryAt = result.retry_at

        if (retryAfter && retryAfter > 0) {
          startCountdown(retryAfter)
        }
        else if (retryAt) {
          const now = new Date()
          const retryTime = new Date(retryAt)
          const diffSeconds = Math.max(0, Math.floor((retryTime.getTime() - now.getTime()) / 1000))

          if (diffSeconds > 0) {
            startCountdown(diffSeconds)
          }
        }
      }
      else if (status === 'blocked') {
        const retryAfter = result?.retry_after_seconds
        const retryAt = result?.retry_at
        const reason = result?.reason

        let description = 'Aguarde um momento antes de tentar novamente.'

        if (retryAfter) {
          const minutes = Math.ceil(retryAfter / 60)
          description = `Aguarde ${minutes} minuto${minutes > 1 ? 's' : ''} antes de tentar novamente.`

          // Inicia o cronômetro se temos retry_after_seconds
          startCountdown(retryAfter)
        }
        else if (retryAt) {
          const retryDate = date(retryAt).format('HH:mm')
          description = `Você poderá reenviar a solicitação às ${retryDate}.`

          // Calcula a diferença em segundos até o horário de retry
          const now = new Date()
          const retryTime = new Date(retryAt)
          const diffSeconds = Math.max(0, Math.floor((retryTime.getTime() - now.getTime()) / 1000))

          if (diffSeconds > 0) {
            startCountdown(diffSeconds)
          }
        }

        if (reason) {
          let reasonText = ''

          switch (reason) {
            case 'daily_quota_reached':
              reasonText = 'Limite diário de solicitações atingido.'
              break
            case 'rate_limit':
              reasonText = 'Muitas tentativas em pouco tempo.'
              break
            case 'success':
              reasonText = 'Solicitação enviada com sucesso.'
              break
            default:
              reasonText = reason
          }

          description = `${reasonText} ${description}`
        }

        showErrorToast('Solicitação bloqueada temporariamente', description)
      }
      else {
        showErrorToast('Erro ao reenviar solicitação. Tente novamente.')
      }
    }
  }
  catch (error) {
    console.error('Error resending WhatsApp:', error)
    showErrorToast('Erro ao reenviar solicitação. Tente novamente.')
  }
  finally {
    isResendingWhatsApp.value = false
  }
}

async function handleCustomerRejectionReset() {
  if (isDeletingSection.value) return
  try {
    isDeletingSection.value = true

    const errorConfig = getErrorConfig('REJECTED_BY_CUSTOMER')
    if (errorConfig) {
      setLastError({
        title: errorConfig.customHeaderTitle,
        text: errorConfig.customHeaderDescription,
      })
    }

    await resetCustomerRejection()
    await new Promise(resolve => setTimeout(resolve, 100))
    handleStepChange(steps.LABELS)
  }
  catch (error) {
    console.error('Failed to reset customer rejection:', error)
    setLastError({
      title: 'Ocorreu um Erro',
      text: 'Não foi possível reiniciar o processo. Por favor, atualize a página e tente novamente.',
    })
    handleStepChange(steps.LABELS)
  }
  finally {
    isDeletingSection.value = false
  }
}

async function handleFatalErrorAndReset() {
  if (isDeletingSection.value) return
  try {
    isDeletingSection.value = true

    const errorConfig = stepInError.value ? getErrorConfig(stepInError.value) : null
    if (errorConfig) {
      setLastError({
        title: errorConfig.customHeaderTitle,
        text: errorConfig.customHeaderDescription,
      })
    }

    await sectionDelete()
    await new Promise(resolve => setTimeout(resolve, 100))
    handleStepChange(steps.LABELS)
  }
  catch (error) {
    console.error('Failed to reset section on fatal error:', error)
    setLastError({
      title: 'Ocorreu um Erro',
      text: 'Não foi possível reiniciar o processo. Por favor, atualize a página e tente novamente.',
    })
    handleStepChange(steps.LABELS)
  }
  finally {
    isDeletingSection.value = false
  }
}

watch(stepInError, (error) => {
  if (error && isFatalError(error)) {
    handleFatalErrorAndReset()
  }
}, { immediate: true })

// Lifecycle
watch(currentStage, (stage) => {
  scrollToCurrentStep(stage ?? 0)
}, { immediate: true })

onMounted(() => {
  scrollToCurrentStep(currentStage.value ?? 0)
})

onUnmounted(() => {
  stopCountdown()
})
</script>

<template>
  <div v-if="currentStage === 3" class="flex flex-col gap-4 w-full mb-4xs">
    <!-- <div class="w-full py-2xs px-4xs rounded-sm bg-neutral-high-pure">
      <InstallationStepMessage
        :status-installation="getSuccessBanner()"
        :highlight="true"
      />
    </div> -->
  </div>

  <div class="installation-page">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex flex-col justify-center items-center w-full h-[40vh] gap-2">
      <div class="spinner-container" />
      <span class="text-xs text-neutral-low-light mt-2xs">
        Carregando informações...
      </span>
    </div>

    <!-- Main content -->
    <div v-else>
      <h1 class="font-highlight text-sm text-neutral-low-dark">
        {{ t('inversorInstallation.form.title') }}
      </h1>

      <SolDivider thickness="x-small" orientation="horizontal" />

      <div class="flex flex-col gap-6 py-2xs w-full">
        <div v-if="currentStage < 3" class="flex flex-col gap-2 leading-xs text-center">
          <h2 class="font-highlight text-xs text-neutral-low-dark">
            {{ headerTexts.title }}
          </h2>

          <p class="text-neutral-low-light w-[80%] mx-auto" v-html="headerTexts.description" />
        </div>

        <SolSteps id="analysis-installation" :steps="stepData" />

        <div
          ref="status-container"
          class="status-container flex gap-5 items-center my-10 overflow-x-scroll"
        >
          <div
            v-for="item in stepsPage"
            :id="`status-${item.stage}`"
            :key="item.stage"
            class="status-item flex flex-col items-center"
            :style="{
              opacity: item.stage === currentStage ? 1 : 0.5,
              filter: item.stage === currentStage || item.stage < currentStage ? 'none' : 'grayscale(1)',
            }"
          >
            <div class="status-image-container" :style="{ backgroundImage: `url(${item.icon})` }">
              <span
                v-if="item.stage === currentStage && currentStage < 3 && !item.stepState?.errorConfig?.hideAnalysisTag"
                class="bg-brand-secondary-pure rounded-lg text-neutral-high-pure text-2xs px-2 py-1 position-absolute top-0 start-50 translate-middle-x"
              >
                em análise
              </span>
            </div>

            <h4 class="font-highlight text-2xs leading-xs text-neutral-low-dark mt-2">
              <span v-if="item.stage < currentStage" class="flex flex-row gap-1">
                <CheckIcon class="icon-size-small" />
                {{ item.title.done }}
              </span>
              <span v-else>
                {{ item.title.waiting }}
              </span>
            </h4>

            <span v-if="item.stage === currentStage" class="mt-quark text-micro text-center font-bold leading-sm">
              <span>
                {{ item.stepState?.errorConfig?.customTimestampText || 'Última mensagem enviada em' }}
                <br>
              </span>
              {{ formattedDate }}
            </span>
          </div>
        </div>

        <SolDivider thickness="x-small" orientation="horizontal" class="w-full" />

        <div class="flex flex-row gap-2 justify-end">
          <SolButton
            v-if="false"
            id="suport-button"
            variant="tertiary"
            size="medium"
            @click="handleSupportClick"
          >
            <template #icon-left>
              <SuportIcon class="icon-size-medium" />
            </template>
            Dúvidas sobre instalação
          </SolButton>

          <div class="flex flex-col items-center gap-1">
            <SolButton
              v-if="showWhatsAppButton"
              id="whatsapp-button"
              variant="primary"
              size="medium"
              :loading="isResendingWhatsApp"
              :disabled="isResendingWhatsApp || isBlocked"
              @click="handleWhatsAppClick"
            >
              <template #icon:left>
                <img src="/icons/whatsapp-white.svg" class="w-6 h-6" alt="whatsapp">
              </template>
              Reenviar Solicitação
            </SolButton>

            <span
              v-if="showCountdown"
              class="text-3xs text-neutral-low-light flex items-center gap-1"
            >
              <RefreshIcon class="icon-size-small animate-spin" />
              Aguarde {{ countdownFormatted }} para reenviar
            </span>
          </div>

          <SolButton
            v-if="currentStage === 2 && stepInError === 'REJECTED_BY_CUSTOMER'"
            id="whatsapp-button"
            variant="primary"
            size="medium"
            :loading="isDeletingSection"
            :disabled="isDeletingSection"
            @click="handleCustomerRejectionReset"
          >
            <template #icon:left>
              <RefreshIcon class="icon-size-medium" />
            </template>
            Refazer Análise
          </SolButton>

          <SolButton
            v-if="currentStage === 3"
            id="projects-button"
            variant="primary"
            size="medium"
            @click="redirectProjects"
          >
            Ir para meus projetos
          </SolButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.installation-page {
  @apply flex flex-col items-stretch gap-2;
  @apply w-full py-2xs px-4xs rounded-sm;
  @apply bg-neutral-high-pure;

  @screen md:site {
    @apply py-xs px-2xs rounded-lg;
  }
}

.status-container {
  justify-content: space-around;
  align-items: flex-start;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

.status-item {
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  @media screen and (max-width: 768px) {
    width: 100dvw;
  }
}

.status-image-container {
  position: relative;
  width: 250px;
  height: 250px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  @media screen and (max-width: 768px) {
    width: 100dvw;
  }
}

.spinner-container {
  position: relative;
  background-image: url('/images/installation/spinner-inner.png');
  background-size: 50%;
  background-position: center;
  background-repeat: no-repeat;
  width: 100px;
  height: 100px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 3px;
    left: 0;
    background-image: url('/images/installation/spinner-outer.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    animation: spin 1s linear infinite;
    transform-origin: center;
  }
}
</style>

<style>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
